import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { config } from 'src/config';
import { Error } from 'src/modules/error';
import { ErrorScope, ErrorTagName } from 'src/shared/constants';
import { EventType, emitPageAction } from 'src/shared/telemetry';

interface SentryProps {
  dsn?: string;
  children: ReactNode;
}
const HOVER_EMAIL = /hoverinc.to|hover.to/;
const FITLERED_ERRORS = /Missing metadataUrl/;

const filterEvents = (event: Sentry.Event, hint?: Sentry.EventHint) => {
  const exception = hint && hint.originalException;
  let errorMessage;

  if (exception) {
    errorMessage =
      typeof exception === 'string' ? exception : exception.message;
  }

  const { user } = event;
  const eventTags = event.tags ?? {};

  const isHoverUser = user?.email?.match(HOVER_EMAIL);
  const isFilterableError = errorMessage?.match(FITLERED_ERRORS);

  const skipSentry = isHoverUser && isFilterableError;
  const skipNewRelic =
    skipSentry || eventTags[ErrorTagName.SCOPE] !== ErrorScope.DESIGN_STUDIO;

  if (!skipNewRelic) {
    const designStudioMode = eventTags[
      ErrorTagName.DESIGN_STUDIO_MODE
    ] as string;

    const attrs = {
      designStudioMode,
    };

    emitPageAction(EventType.RUNTIME_ERROR, attrs);
  }

  // if user is an internal user and a filterable error
  // do not send to Sentry
  if (skipSentry) {
    return null;
  }
  return event;
};

const initializeSentry = (dsn: string) => {
  void Sentry.close().then(() => {
    Sentry.init({
      dsn,
      environment: config.NODE_ENV,
      release: config.VERSION,
      beforeSend: filterEvents,
      ignoreErrors: [
        'Non-Error exception captured', // see https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/13
        'Non-Error promise rejection captured', // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/13

        //---------------------------------
        // Sentry recommended block list https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
        //---------------------------------
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        'fbq', // Facebook pixel tracking weirdness
      ],
      denyUrls: [
        //---------------------------------
        // Sentry recommended block list https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
        //---------------------------------
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      ],
    });
  });
};

const setSentryUser = (userId: number, email?: string) => {
  Sentry.setUser({ id: userId.toString(), email });
};

const LoggingProvider = ({ dsn, children }: SentryProps) => {
  const isConfigured = Boolean(
    (process.env.RAZZLE_GIT_HEAD_SHA as string) && dsn,
  );

  useEffect(() => {
    if (!isConfigured || !dsn) return;
    void initializeSentry(dsn);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsn]);

  if (isConfigured) {
    return (
      // https://docs.sentry.io/platforms/javascript/react/#error-boundaries
      <Sentry.ErrorBoundary fallback={Error}>{children}</Sentry.ErrorBoundary>
    );
  }

  return <ErrorBoundary FallbackComponent={Error}>{children}</ErrorBoundary>;
};

export { LoggingProvider as Sentry, filterEvents, setSentryUser };
